import React, { useState, useEffect } from 'react';
import styles from './WhatsAppVerificationPage.module.css';

export default function WhatsAppVerificationPage({ whatsapp, authToken, onVerified, onLogout }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('info');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // Auto-send verification code on mount
  useEffect(() => {
    if (!codeSent) {
      startVerification();
    }
  }, []);

  function showMessage(text, type = 'info') {
    setMessage(text);
    setMessageType(type);
  }

  async function startVerification() {
    setMessage(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify/phone/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ whatsapp }),
      });
      const data = await res.json();
      if (data?.sucesso) {
        setCodeSent(true);
        setCountdown(60);
        showMessage('Código enviado para o seu WhatsApp.', 'sucesso');
      } else {
        showMessage(data?.erro || 'Não foi possível enviar o código.', 'erro');
      }
    } catch (e) {
      showMessage('Erro ao tentar enviar o código.', 'erro');
    } finally {
      setLoading(false);
    }
  }

  async function confirmVerification() {
    if (!code) return;
    setConfirmLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify/phone/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ whatsapp, code }),
      });
      const data = await res.json();
      if (data?.success) {
        if (typeof onVerified === 'function') await onVerified(data);
      } else {
        showMessage(data?.erro || 'Código inválido ou expirado.', 'erro');
      }
    } catch (e) {
      showMessage('Erro ao confirmar verificação.', 'erro');
    } finally {
      setConfirmLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && code.length >= 4 && !confirmLoading) {
      confirmVerification();
    }
  }

  const messageClass =
    messageType === 'sucesso'
      ? styles.messageSucesso
      : messageType === 'erro'
      ? styles.messageErro
      : styles.messageInfo;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="/" className={styles.brand}>Analise Central</a>
        <button className={styles.logoutButton} onClick={onLogout} type="button">
          Sair
        </button>
      </header>

      <main className={styles.shell}>
        {/* Hero */}
        <section className={styles.hero}>
          <span className={styles.badge}>Verificação</span>
          <h1 className={styles.title}>
            Confirme seu <span className={styles.titleAccent}>WhatsApp</span>
          </h1>
          <p className={styles.description}>
            Um código de 6 dígitos será enviado para{' '}
            <span className={styles.phoneHighlight}>{whatsapp}</span>.
            Digite-o abaixo para validar sua conta.
          </p>
        </section>

        {/* Card */}
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <span className={styles.cardKicker}>Verificação de propriedade</span>
              <h2>Código de segurança</h2>
            </div>
          </div>

          {/* Steps indicator */}
          <div className={styles.steps}>
            <div className={`${styles.step} ${codeSent ? '' : styles.stepActive}`}>
              <span className={styles.stepDot} />
              Enviar código
            </div>
            <div className={styles.stepLine} />
            <div className={`${styles.step} ${codeSent ? styles.stepActive : ''}`}>
              <span className={styles.stepDot} />
              Confirmar
            </div>
          </div>

          <div className={styles.form}>
            {/* Send code button */}
            <div className={styles.actionsRow}>
              <button
                onClick={startVerification}
                disabled={loading || countdown > 0}
                className={styles.actionButton}
                type="button"
              >
                {loading ? (
                  <>
                    <span className={styles.spinnerSmall} />
                    Enviando...
                  </>
                ) : countdown > 0 ? (
                  `Reenviar em ${countdown}s`
                ) : codeSent ? (
                  '📲 Reenviar código'
                ) : (
                  '📲 Enviar código de verificação'
                )}
              </button>
            </div>

            {/* Code input */}
            <label className={styles.field}>
              <span>Código de 6 dígitos</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={handleKeyDown}
                autoComplete="one-time-code"
                disabled={!codeSent}
              />
            </label>

            {/* Confirm button */}
            <button
              onClick={confirmVerification}
              className={styles.submitButton}
              disabled={!codeSent || code.length < 4 || confirmLoading}
              type="button"
            >
              {confirmLoading ? (
                <>
                  <span className={styles.spinner} />
                  Verificando...
                </>
              ) : (
                'Confirmar verificação →'
              )}
            </button>

            {/* Message */}
            {message && (
              <div className={`${styles.message} ${messageClass}`}>
                {message}
              </div>
            )}

            {/* Resend hint */}
            {codeSent && countdown > 0 && (
              <div className={styles.resendRow}>
                Não recebeu? Aguarde {countdown}s para reenviar.
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}